// Cross-resource link helpers. Reads/writes the `resource_links` table so
// chat channels, calendar events, and meet rooms can reference each other.
//
// All functions support both direct calls and `el.call('fn', ...)` — root is
// inferred from `this.state.root` when the user passes a channel/event/room.
import { getSDK } from '../../shared/functions/sdk.js'

export const RESOURCE_TYPE = Object.freeze({
  CHAT_CHANNEL: 'chat_channel',
  MEET_ROOM: 'meet_room',
  CALENDAR_EVENT: 'calendar_event'
})
const _VALID_TYPES = new Set(Object.values(RESOURCE_TYPE))

// (type, id) pairs are sorted lexicographically so (A, B) and (B, A) collapse
// to the same row server-side (enforced by the CHECK constraint).
const _order = (aType, aId, bType, bId) => {
  const a = `${aType}\x00${aId}`
  const b = `${bType}\x00${bId}`
  return a < b
    ? [aType, aId, bType, bId]
    : [bType, bId, aType, aId]
}

const _asId = (x) => (x == null ? null : String(x))

// Per-(links array identity) cache for getLinkedResources results. The cache
// is invalidated automatically whenever `root.resourceLinks` is replaced
// (link/unlink optimistic updates always produce a new array reference).
const _linksCache = new WeakMap()

export async function linkResources (aType, aId, bType, bId) {
  const root = this?.state?.root
  const me = root?.currentUserEmail
  if (!aType || aId == null || !bType || bId == null) return null
  if (aType === bType && _asId(aId) === _asId(bId)) return null
  const [t1, i1, t2, i2] = _order(aType, _asId(aId), bType, _asId(bId))
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.documents.resourceLinks create
    // Wrapper handles upsert-on-(a_type,a_id,b_type,b_id) — same unique
    // constraint enforces dedup server-side.
    const result = await getSDK().execute(
      'workspaceProject.documents.resourceLinks', 'create',
      { payload: { a_type: t1, a_id: i1, b_type: t2, b_id: i2, created_by: me } }
    )
    const data = Array.isArray(result?.data)
      ? result.data[0]
      : (result?.data ?? result ?? null)
    if (!data) return null
    // Optimistic state merge.
    if (root) {
      const cur = root.resourceLinks || []
      const exists = cur.some((r) => r.id === data.id)
      if (!exists) root.update({ resourceLinks: [...cur, data] })
    }
    return data
  } catch (err) {
    console.error('[linkResources]', err)
    return null
  }
}

export async function unlinkResources (aType, aId, bType, bId) {
  const root = this?.state?.root
  const [t1, i1, t2, i2] = _order(aType, _asId(aId), bType, _asId(bId))
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.documents.resourceLinks removeByPair
    // The composite-key delete path — the row id isn't known at the call
    // site, so we pass the (a_type, a_id, b_type, b_id) tuple as a filter
    // and let the server resolve the row.
    await getSDK().execute(
      'workspaceProject.documents.resourceLinks', 'removeByPair',
      { filter: { a_type: t1, a_id: i1, b_type: t2, b_id: i2 } }
    )
    if (root) {
      const cur = root.resourceLinks || []
      root.update({
        resourceLinks: cur.filter(
          (r) => !(r.a_type === t1 && r.a_id === i1 && r.b_type === t2 && r.b_id === i2)
        )
      })
    }
    return true
  } catch (err) {
    console.error('[unlinkResources]', err?.message || err)
    return false
  }
}

// Return every resource linked to (type, id). Memoized by
// `root.resourceLinks` array identity so repeated calls in the same render
// return a stable reference (cheap re-reads in reactive factories).
export function getLinkedResources (rootOrType, maybeId, maybeType) {
  let root, type, id
  if (typeof rootOrType === 'string') {
    root = this?.state?.root
    type = rootOrType
    id = maybeId
  } else {
    root = rootOrType
    type = maybeId
    id = maybeType
  }
  const links = (root && root.resourceLinks) || null
  if (!links) return []
  let byKey = _linksCache.get(links)
  if (!byKey) { byKey = new Map(); _linksCache.set(links, byKey) }
  const sid = _asId(id)
  const key = `${type}\x00${sid}`
  const cached = byKey.get(key)
  if (cached) return cached
  const out = []
  for (const r of links) {
    if (r.a_type === type && _asId(r.a_id) === sid) {
      out.push({ type: r.b_type, id: r.b_id, linkId: r.id })
    } else if (r.b_type === type && _asId(r.b_id) === sid) {
      out.push({ type: r.a_type, id: r.a_id, linkId: r.id })
    }
  }
  byKey.set(key, out)
  return out
}

// Back-fill existing meet_rooms links (calendar_event_id / chat_channel_id)
// into resource_links. Safe to re-run — upserts on the unique key.
export async function backfillMeetRoomLinks () {
  const root = this?.state?.root
  const ops = []
  for (const r of (root?.rooms || [])) {
    if (r.calendarEventId != null) {
      ops.push(linkResources.call(this, 'meet_room', r.id, 'calendar_event', r.calendarEventId))
    }
    if (r.chatChannelId != null) {
      ops.push(linkResources.call(this, 'meet_room', r.id, 'chat_channel', r.chatChannelId))
    }
  }
  await Promise.all(ops)
}
